import { useQuery } from '@tanstack/react-query';
import { homepageSectionService, HomepageSectionSettings, CarouselSlide } from '@/services/homepageSectionService';

export function useHomepageSectionSettings() {
  const {
    data: settings,
    isLoading: isLoadingSettings,
    error: settingsError,
    refetch: refetchSettings,
  } = useQuery<HomepageSectionSettings>({
    queryKey: ['homepage-section-settings'],
    queryFn: homepageSectionService.getSettings,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const {
    data: slides,
    isLoading: isLoadingSlides,
    error: slidesError,
    refetch: refetchSlides,
  } = useQuery<CarouselSlide[]>({
    queryKey: ['homepage-carousel-slides'],
    queryFn: homepageSectionService.getCarouselSlides,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    enabled: settings?.sectionMode === 'carousel',
  });

  return {
    settings: settings || { sectionMode: 'tutors' as const },
    slides: slides || [],
    isLoading: isLoadingSettings || (settings?.sectionMode === 'carousel' && isLoadingSlides),
    error: settingsError || slidesError,
    refetch: () => {
      refetchSettings();
      refetchSlides();
    },
  };
}

// Admin hook - fetches all slides including disabled ones
export function useHomepageSectionAdmin() {
  const {
    data: settings,
    isLoading: isLoadingSettings,
    refetch: refetchSettings,
  } = useQuery<HomepageSectionSettings>({
    queryKey: ['homepage-section-settings'],
    queryFn: homepageSectionService.getSettings,
    staleTime: 60000, // 1 minute for admin
    refetchOnWindowFocus: true,
  });

  const {
    data: allSlides,
    isLoading: isLoadingSlides,
    refetch: refetchSlides,
  } = useQuery<CarouselSlide[]>({
    queryKey: ['homepage-carousel-slides-admin'],
    queryFn: homepageSectionService.getAllCarouselSlides,
    staleTime: 60000, // 1 minute for admin
    refetchOnWindowFocus: true,
  });

  return {
    settings: settings || { sectionMode: 'tutors' as const },
    allSlides: allSlides || [],
    isLoading: isLoadingSettings || isLoadingSlides,
    refetch: () => {
      refetchSettings();
      refetchSlides();
    },
  };
}
